import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import DiSCResults from '../components/DiSCResults';
import MBTIResults from '../components/MBTIResults';
import api from '../sever/Api';

const TestReportAdmin = () => {
  const { id } = useParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/api/admin/tests/${id}`);
        setData(res.data?.testResult || null);
      } catch (e) {
        setError('Failed to load report');
      } finally {
        setLoading(false);
      }
    };
    if (id) run();
  }, [id]);

  const formatDate = (d) => { try { return new Date(d).toLocaleString(); } catch { return '-'; } };
  const getTestTypeLabel = (type) => {
    const map = {
      competency: 'Competency',
      bigfive: 'Big Five',
      viacharacter: 'VIA Character',
      high5: 'High5',
      emotionalintelligence: 'Emotional Intelligence',
      disc: 'DiSC',
      ikigai: 'Ikigai',
      johari: 'Johari',
      mbti: 'MBTI',
      learningstyle: 'Learning Style',
      enneagram: 'Enneagram',
      sevenquestion: 'Essence Echo',
    };
    return map[String(type || '').toLowerCase()] || type || '-';
  };

  const renderBarList = (items) => (
    <div className="space-y-3">
      {items.map((it) => (
        <div key={it.key} className="text-sm">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium text-gray-800">{it.key}</span>
            <span className="font-semibold text-gray-700">{Math.round(it.value)}%</span>
          </div>
          <div className={`w-full ${it.track || 'bg-gray-200'} rounded-full h-3`}>
            <div className={`${it.color || 'bg-blue-500'} h-3 rounded-full`} style={{ width: `${Math.max(0, Math.min(100, Number(it.value) || 0))}%` }}></div>
          </div>
        </div>
      ))}
    </div>
  );

  const tryObjectToBars = (obj, labelMap) => {
    if (!obj || typeof obj !== 'object') return null;
    const entries = Object.entries(obj)
      .filter(([, v]) => typeof v === 'number')
      .map(([k, v]) => ({ key: (labelMap && labelMap[k]) || k, value: v }));
    if (!entries.length) return null;
    return renderBarList(entries);
  };

  const renderScores = (result) => {
    if (!result) return null;
    const type = String(result.testType || '').toLowerCase();
    const scores = result.scores || {};

    if (type === 'disc') {
      const d = scores?.discScores?.dPercentage ?? scores?.d?.percentage ?? scores?.d ?? 0;
      const i = scores?.discScores?.iPercentage ?? scores?.i?.percentage ?? scores?.i ?? 0;
      const s = scores?.discScores?.sPercentage ?? scores?.s?.percentage ?? scores?.s ?? 0;
      const c = scores?.discScores?.cPercentage ?? scores?.c?.percentage ?? scores?.c ?? 0;
      const items = [
        { key: 'D - Dominance', value: d, color: 'bg-red-500', track: 'bg-red-200' },
        { key: 'I - Influence', value: i, color: 'bg-yellow-500', track: 'bg-yellow-200' },
        { key: 'S - Steadiness', value: s, color: 'bg-green-500', track: 'bg-green-200' },
        { key: 'C - Conscientiousness', value: c, color: 'bg-blue-500', track: 'bg-blue-200' },
      ];
      return renderBarList(items);
    }

    if (type === 'bigfive') {
      const map = {
        openness: scores?.openness ?? scores?.O,
        conscientiousness: scores?.conscientiousness ?? scores?.C,
        extraversion: scores?.extraversion ?? scores?.E,
        agreeableness: scores?.agreeableness ?? scores?.A,
        neuroticism: scores?.neuroticism ?? scores?.N,
      };
      const items = Object.entries(map)
        .filter(([, v]) => typeof v === 'number')
        .map(([k, v]) => ({ key: k.charAt(0).toUpperCase() + k.slice(1), value: v }));
      if (items.length) return renderBarList(items);
    }

    if (type === 'mbti') {
      const typeLabel = scores?.type || result?.overallType || '';
      const dims = scores?.dimensions || scores?.percentages;
      let items = [];
      if (dims && typeof dims === 'object') {
        const pairs = [['E','I'],['S','N'],['T','F'],['J','P']];
        pairs.forEach(([a,b]) => {
          const av = dims[a] ?? dims[a.toLowerCase()];
          const bv = dims[b] ?? dims[b.toLowerCase()];
          if (typeof av === 'number' && typeof bv === 'number') items.push({ key: `${a} vs ${b}`, value: Math.max(av, bv) });
        });
      }
      return (
        <div className="space-y-3">
          {typeLabel ? <div className="text-sm"><span className="text-gray-600">Type:</span> <span className="font-semibold">{typeLabel}</span></div> : null}
          {items.length ? renderBarList(items) : null}
          {!typeLabel && !items.length ? (
            <pre className="text-xs bg-gray-50 p-3 rounded overflow-x-auto">{JSON.stringify(scores || {}, null, 2)}</pre>
          ) : null}
        </div>
      );
    }

    if (type === 'emotionalintelligence') {
      const obj = scores?.domains || scores;
      const labelMap = { selfAwareness: 'Self Awareness', selfRegulation: 'Self Regulation', motivation: 'Motivation', empathy: 'Empathy', socialSkills: 'Social Skills' };
      const view = tryObjectToBars(obj, labelMap);
      if (view) return view;
    }

    if (type === 'competency') {
      const obj = scores?.competencies || scores;
      const view = tryObjectToBars(obj);
      if (view) return view;
    }

    if (type === 'viacharacter' || type === 'high5') {
      const arr = scores?.topStrengths || scores?.top5 || scores?.strengths;
      if (Array.isArray(arr) && arr.length) {
        return (
          <ul className="list-disc ml-5 text-sm text-gray-800">
            {arr.slice(0, 10).map((s, idx) => (
              <li key={idx}>{typeof s === 'string' ? s : (s.name || s.label || JSON.stringify(s))}</li>
            ))}
          </ul>
        );
      }
      const view = tryObjectToBars(scores);
      if (view) return view;
    }

    if (type === 'learningstyle') {
      const obj = scores?.styles || scores;
      const view = tryObjectToBars(obj);
      if (view) return view;
    }

    if (type === 'enneagram') {
      const obj = scores?.types || scores;
      const labelMap = { type1:'Type 1', type2:'Type 2', type3:'Type 3', type4:'Type 4', type5:'Type 5', type6:'Type 6', type7:'Type 7', type8:'Type 8', type9:'Type 9' };
      const view = tryObjectToBars(obj, labelMap);
      if (view) return view;
    }

    if (type === 'ikigai' || type === 'johari' || type === 'sevenquestion') {
      const view = tryObjectToBars(scores);
      if (view) return view;
    }

    return <pre className="text-xs bg-gray-50 p-3 rounded overflow-x-auto">{JSON.stringify(scores || {}, null, 2)}</pre>;
  };

  return (
    <div className='flex flex-col lg:flex-row min-h-screen w-full overflow-x-hidden'>
      <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      </div>
      <div className='ml-64 flex-1 relative'>
        <Navbar toggleSidebar={toggleSidebar} />
        <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB]">
          <div className="w-full max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Test Report</h1>
            {loading ? (
              <div>Loading...</div>
            ) : error ? (
              <div className="text-red-600 text-sm">{error}</div>
            ) : !data ? (
              <div className="text-gray-600 text-sm">No data</div>
            ) : (
              <>
                {/* Header summary */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-4 mb-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-gray-500">User</div>
                      <div className="font-medium">{[data?.user?.firstName, data?.user?.lastName].filter(Boolean).join(' ') || '-'}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Email</div>
                      <div className="font-medium">{data?.user?.email || '-'}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Test</div>
                      <div className="font-medium">{getTestTypeLabel(data?.testType)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Completed</div>
                      <div className="font-medium">{formatDate(data?.completedAt)}</div>
                    </div>
                  </div>
                </div>

                {/* Render same UI as user results where available */}
                {String(data?.testType || '').toLowerCase() === 'disc' ? (
                  <DiSCResults
                    results={{
                      score: {
                        overallScore: data?.overallScore,
                        percentage: data?.overallPercentage,
                        personalityType: data?.scores?.personalityType,
                        discScores: data?.scores?.discScores || {
                          dPercentage: data?.scores?.d?.percentage ?? data?.scores?.d ?? 0,
                          iPercentage: data?.scores?.i?.percentage ?? data?.scores?.i ?? 0,
                          sPercentage: data?.scores?.s?.percentage ?? data?.scores?.s ?? 0,
                          cPercentage: data?.scores?.c?.percentage ?? data?.scores?.c ?? 0,
                        },
                        d: data?.scores?.d,
                        i: data?.scores?.i,
                        s: data?.scores?.s,
                        c: data?.scores?.c,
                      },
                      takenAt: data?.completedAt,
                    }}
                    onRetakeTest={() => {}}
                  />
                ) : String(data?.testType || '').toLowerCase() === 'mbti' ? (
                  <MBTIResults
                    results={{
                      score: {
                        mbtiScores: data?.scores?.mbtiScores || {
                          extraversion: data?.scores?.extraversion,
                          introversion: data?.scores?.introversion,
                          sensing: data?.scores?.sensing,
                          intuition: data?.scores?.intuition,
                          thinking: data?.scores?.thinking,
                          feeling: data?.scores?.feeling,
                          judging: data?.scores?.judging,
                          perceiving: data?.scores?.perceiving,
                        },
                        personalityType: data?.scores?.mbtiType || data?.scores?.type,
                        mbtiType: data?.scores?.mbtiType || data?.scores?.type,
                      },
                      takenAt: data?.completedAt,
                    }}
                    onRetakeTest={() => {}}
                  />
                ) : (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-4">
                    <div className="border-t pt-4">
                      <h2 className="text-lg font-semibold mb-2">Scores</h2>
                      {renderScores(data)}
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold mb-2">Overall</h2>
                      <div className="text-sm">Score: {data?.overallScore ?? '-'}</div>
                      <div className="text-sm">Percentage: {data?.overallPercentage ?? '-'}</div>
                      <div className="text-sm">Time Taken: {data?.timeTaken ? `${data.timeTaken} min` : '-'}</div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestReportAdmin;


